import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Icon } from "@/lib/icons";
import { Markdown } from "@/lib/markdown";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, XCircle, FileText, Dumbbell, BookOpen } from "lucide-react";

export default function Frameworks() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/frameworks").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const openFramework = async (id) => {
    setOpen(true); setLoading(true); setActive(null);
    try { const { data } = await api.get(`/frameworks/${id}`); setActive(data); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8" data-testid="frameworks-page">
      <div>
        <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">Framework Library</span>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Think with the best mental models.</h1>
        <p className="text-slate-400 mt-2">12 battle-tested business frameworks — when to use them, when not to, with templates and exercises.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((f) => (
          <button key={f.id} data-testid={`framework-${f.id}`} onClick={() => openFramework(f.id)} className="eco-card eco-card-hover p-6 text-left">
            <div className="flex items-center justify-between">
              <div className="h-11 w-11 rounded-xl bg-[#0055FF]/15 border border-[#0055FF]/30 flex items-center justify-center"><Icon name={f.icon} size={22} className="text-[#4d8bff]" /></div>
              <span className="eco-tag">{f.category}</span>
            </div>
            <h3 className="font-display text-lg font-semibold text-white mt-4">{f.name}</h3>
            <p className="text-sm text-slate-400 mt-1.5 line-clamp-2">{f.definition}</p>
          </button>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{active?.name || "Loading…"}</DialogTitle></DialogHeader>
          {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#4d8bff]" /></div> : active && (
            <div className="space-y-5">
              <p className="text-slate-300">{active.definition}</p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500 mb-1">Purpose</div>
                <p className="text-sm text-slate-300">{active.purpose}</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="eco-card p-4">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-white mb-3"><CheckCircle2 size={15} className="text-emerald-400" /> When to use</h4>
                  <ul className="space-y-2">{active.when_to_use.map((x, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 mt-2 shrink-0" />{x}</li>)}</ul>
                </div>
                <div className="eco-card p-4">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-white mb-3"><XCircle size={15} className="text-red-400" /> When NOT to use</h4>
                  <ul className="space-y-2">{active.when_not.map((x, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="h-1.5 w-1.5 rounded-full bg-red-400 mt-2 shrink-0" />{x}</li>)}</ul>
                </div>
              </div>
              <div>
                <h4 className="flex items-center gap-2 text-sm font-semibold text-white mb-2"><BookOpen size={15} className="text-[#4d8bff]" /> Real examples</h4>
                <ul className="space-y-2">{active.examples.map((x, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#0055FF] mt-2 shrink-0" />{x}</li>)}</ul>
              </div>
              <div>
                <h4 className="flex items-center gap-2 text-sm font-semibold text-white mb-2"><FileText size={15} className="text-amber-400" /> Template</h4>
                <pre className="bg-black/60 border border-white/10 rounded-xl p-4 text-sm text-slate-300 whitespace-pre-wrap font-mono-eco">{active.template}</pre>
              </div>
              <div className="bg-[#0055FF]/10 border border-[#0055FF]/20 rounded-xl p-4">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-white mb-2"><Dumbbell size={15} className="text-[#4d8bff]" /> Try it now</h4>
                <p className="text-sm text-slate-300">{active.exercise}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
